import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SupabaseService } from '../../services/supabase.service';
import { environment } from 'src/environments/environment';

/// <summary>
/// Login component using Supabase authentication
/// </summary>
@Component({
  selector: 'app-supabase-login',
  templateUrl: './supabase-login.component.html',
  styleUrls: ['./supabase-login.component.scss']
})
export class SupabaseLoginComponent implements OnInit {
  loading = false;
  returnUrl: string = '/';

  constructor(
    private supabaseService: SupabaseService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Get return URL from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    // Check if user is already authenticated
    this.supabaseService.user$.subscribe(user => {
      if (user && !this.loading) {
        this.showToast('success', 'Connexion réussie', `Bienvenue, ${user.email || 'utilisateur'}!`);
        
        setTimeout(() => {
          this.router.navigate([this.returnUrl]);
        }, 1000);
      }
    });
  }

  /// <summary>
  /// Handles Azure SSO login (only supported authentication method)
  /// </summary>
  async signInWithAzure(): Promise<void> {
    try {
      this.loading = true;
      await this.supabaseService.signInWithAzure(this.returnUrl);
      
      // Note: User will be redirected to Azure, then back to the app
      // The success message will be handled by the auth state change
      
    } catch (error: any) {
      console.error('Azure login error:', error);
      
      this.showToast('error', 'Erreur de connexion', 'Erreur lors de la connexion avec Azure AD');
      this.loading = false;
    }
  }

  /// <summary>
  /// Shows a toast message (simple implementation without PrimeNG)
  /// </summary>
  private showToast(severity: 'success' | 'error' | 'info' | 'warn', summary: string, detail: string): void {
    const toastContainer = document.getElementById('toast-container');
    if (!toastContainer) return;

    const toast = document.createElement('div');
    toast.className = `toast toast-${severity}`;
    toast.innerHTML = `
      <div class="toast-content">
        <div class="toast-header">
          <span class="toast-icon">${this.getToastIcon(severity)}</span>
          <strong class="toast-summary">${summary}</strong>
        </div>
        <div class="toast-detail">${detail}</div>
      </div>
    `;

    // Add styles for toast
    if (!document.getElementById('toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        .toast {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          margin-bottom: 1rem;
          padding: 1rem;
          min-width: 300px;
          animation: slideIn 0.3s ease-out;
          border-left: 4px solid;
        }
        .toast-success { border-left-color: #10b981; }
        .toast-error { border-left-color: #ef4444; }
        .toast-info { border-left-color: #3b82f6; }
        .toast-warn { border-left-color: #f59e0b; }
        .toast-content { display: flex; flex-direction: column; gap: 0.5rem; }
        .toast-header { display: flex; align-items: center; gap: 0.5rem; }
        .toast-icon { font-size: 1.2rem; }
        .toast-summary { font-weight: 600; color: #1f2937; }
        .toast-detail { color: #6b7280; font-size: 0.9rem; }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    toastContainer.appendChild(toast);

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);
  }

  /// <summary>
  /// Gets the appropriate icon for toast severity
  /// </summary>
  private getToastIcon(severity: string): string {
    switch (severity) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      default: return 'ℹ️';
    }
  }

  protected getSiteKey(): string {
    return environment.turnstileSiteKey;
  }
}
